import * as React from 'react'
import styled, { useTheme } from 'styled-components'
import { castingStoreAvailableCustomerPlanSubscriptions, castingStoreDisplayPriceWithTax, castingStoreGetTotalPrice, StoreProps } from '../../../../services/stores/casting.store'
import { currencyFormat, currencyWrapTaxNotice } from '../../../../services/utils/currency.service'
import { I18nService } from '../../../../services/utils/i18n.service'
import { formatDurationFromSecs } from '../../../../services/utils/utils.service'
import LoadingComponent from '../../../shared/loading.component'
import { Step, StepContent, StepSummary, StepTitle } from '../casting-styled.component'
import { SupplierBase } from '../../../../services/json-schemas/base/supplier'

const PrimarySpan = styled.span`
  color: ${props => props.theme.colors.clr_1};
`

const Small = styled.i`
  font-weight: normal;
  font-size: 80%;
`

const StepContentPrice = styled(StepContent)`
  @media (min-width: ${props => props.theme.breakpoints.m}px) {
    margin: 0;
  }
`

type Props = StoreProps & {
  styleStepContent?: React.CSSProperties
  styleStep?: React.CSSProperties
}

const i18nScoped = new I18nService(require('./casting-price.component.yml'))

export default function CastingPriceComponent ({
  data,
  store,
  data: { offeredService: os, market }, 
  styleStepContent, 
  styleStep = { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '4.5em' }
}: Props) {
  const theme = useTheme()
  if (!store.canFocus('price') || !os['display_price_casting?']) return null

  const isStartingAt = market.market_type === 'Marketplace' || os.marketplace_price_mode !== 'fixed'
  const rows = computePriceRows({ data, store })

  return <Step $focused={ false } style={ styleStep }>
    <StepContentPrice $focused={ false } style={ styleStepContent }>
      { (rows.length <= 2 || theme.castingPluginLayout !== 'row') && <StepTitle>{ i18nScoped.t(isStartingAt ? 'starting_at' : 'price') }</StepTitle> }
      { rows.map((row, index) => <StepSummary key={ index }>{ row }</StepSummary>) }
    </StepContentPrice>
  </Step>
}

/**
 * Compute content of the price block with minimal formatting, so it can be integrated invarious UI.
 */
export function computePriceRows ({ data, store }: StoreProps): React.ReactNode[] {
  const { offeredService: os, market, promotionalCode, prescriber, quantity, coupon, slots, overrideRatePriceCents, customer, selectedQuantityDiscount, customerPlan, customerPlanSubscriptionExpirationAction, customerPlanSubscriptionRemovePendingId } = data

  if (!os || !os['display_price_casting?']) return []
  const rows: React.ReactNode[] = []
  const noAskedDuration = os.rentable && os.no_start_time_nor_duration_asked_to_customer && (!slots.length || slots[0].duration === null)
  const taxCreditFactor = os.tax_credit_eligible ? (1 - market.tax_credit_config.rate / 100.0) : null
  const displayPriceWithTax = castingStoreDisplayPriceWithTax({ customer, offeredService: os, market })

  const discountPrice = (price: number, type?: 'perHour') => {
    if (selectedQuantityDiscount) {
      return price - (price * selectedQuantityDiscount.discount_percentage / 100)
    } else if (prescriber && prescriber.discount_percentage && !promotionalCode) {
      return price - (price * prescriber.discount_percentage / 100)
    } else if (promotionalCode?.reduction_type === 'percentage' ) {
      return price - (price * promotionalCode.value / 100)
    } else if (promotionalCode?.reduction_type === 'amount' && type !== 'perHour') {
      const discountAmountTaxAdjusted = promotionalCode.vat_included === displayPriceWithTax
        ? promotionalCode.value
        : promotionalCode.vat_included
        ? +((promotionalCode.value / (1 + os.vat_price_percentage / 100)).toFixed(2)) // discount without tax
        : +((promotionalCode.value * (1 + os.vat_price_percentage / 100)).toFixed(2))

      
      return Math.max(0, price - discountAmountTaxAdjusted)
    }

    return null
  }

  /**
   * @param price
   * @param type The kind of offer unit that the price refers to, because discount may be computed another way for hourly rates.
   * @param postDiscountMultiplier In case os.tax_credit_eligible, discount are computed on the base price, then the customer pays one half (in France) of the discounted price.
   */
  const formatPriceWithDiscount = (price: number, type: 'perHour' = undefined, postDiscountMultiplier = 1) => {  
    const discountedPrice = discountPrice(price, type)

    if (typeof discountedPrice === 'number') {
      return <>
        <del>{ currencyFormat(market.currency, (price * postDiscountMultiplier).toFixed(2)) }</del>&nbsp;
        { currencyFormat(market.currency, (discountedPrice * postDiscountMultiplier).toFixed(2)) }
      </>
    }

    return currencyFormat(market.currency, (price * postDiscountMultiplier).toFixed(2))
  }

  if ((os.casting_custom_price && overrideRatePriceCents === null) || os.enable_employee_of_customer) {
    rows.push(<PrimarySpan id="js-test-custom-price">{ i18nScoped.t('custom_price') }</PrimarySpan>)
    rows.push(<Small>{ i18nScoped.t('custom_price_tooltip') }</Small>)
    return rows
  }

  if (noAskedDuration) rows.push(<PrimarySpan id="js-test-no-price">{ i18nScoped.t('duration_on_quotation') }</PrimarySpan>)

  if (os.rentable) {
    if (os.free) {
      rows.push(<PrimarySpan id="js-test-free-service">{ i18nScoped.t('free_service') }</PrimarySpan>)
      return rows
    }

    // The rest of the code is about computing the price of individual events, them summing them up.
    // But when rentable_unit_of_work is 'month', the price does not depend on the number and duration of events.
    // We fallback to another endpoint, that computes the total price without that much details.
    if (os.rentable_unit_of_work === 'month') {
      const sortedTotalPrices = castingStoreGetTotalPrice(store.data, store)

      // eslint-disable-next-line react/jsx-key
      if (!sortedTotalPrices) return [<LoadingComponent presentation="inline" align="center" loadingIconStyle={{ display: 'inline-block', height: '2em' }} />]
      if (!sortedTotalPrices.length) return []

      // The total amount is rendered in big font (PrimarySpan) as it is the most important price and the first visible one.
      // But when os.tax_credit_eligible is true, another price is put in first position and should retain user's focus.
      // Additionnally, the price after tax must be visible in mobile mode, where the number of rows is limitted.
      const Total = os.tax_credit_eligible ? React.Fragment : PrimarySpan
      const totalDisplayedPriceCentsRange = { min: sortedTotalPrices[0].displayedPriceCents, max: sortedTotalPrices[sortedTotalPrices.length - 1].displayedPriceCents }

      rows.push(<>
        <Total>{ currencyWrapTaxNotice(renderPriceInterval(market, totalDisplayedPriceCentsRange, cents => formatPriceWithDiscount(cents / 100)), displayPriceWithTax) }</Total>&nbsp;
        <Small>({ i18nScoped.t('total_per_month') })</Small>
      </>)

      if (os.tax_credit_eligible) {
        rows.push(<>
          <PrimarySpan>{ currencyWrapTaxNotice(renderPriceInterval(market, totalDisplayedPriceCentsRange, cents => formatPriceWithDiscount(cents / 100, undefined, taxCreditFactor)), displayPriceWithTax) }</PrimarySpan>&nbsp;
          <Small>({ i18nScoped.t('after_tax_reduction', { rate: market.tax_credit_config.rate }) })</Small>
        </>)
      }

      return rows
    }

    // If the prescriber is funding the casting, they are a limited count of hours available.
    const prescriberRemainingFundedHours = store.getPrescriberRemainingFundedHours()
    if (typeof prescriberRemainingFundedHours === 'number') {
      const occurrences = store.simulateOccurrencesUpToMaxAllowedDuration(prescriberRemainingFundedHours)
      rows.push(i18nScoped.t('remaining_hours', { asked: formatDurationFromSecs(occurrences.reduce((acc, curr) => acc + curr.durationWithOptions, 0)) || '?', total: prescriberRemainingFundedHours }))
      if (occurrences.length > 0) rows.push(<Small>{ i18nScoped.t('counted_events', { count: occurrences.length }) } { prescriber.name }</Small>)
      return rows
    }

    // The customer has an unfinalized subcription purchase: display its price
    const pendingCustomerPlanSubscription = castingStoreAvailableCustomerPlanSubscriptions({ customer, offeredService: os }).find(_ => !_.validated_at && _.id != customerPlanSubscriptionRemovePendingId)

    // If there is an available customer plan subscriptions that covers the service, the casting is funded by customer plan.
    // expiration_action == 'keep_events' means the subscription expires soon and the customer will not renew it, while keeping the events scheduled.
    if (!pendingCustomerPlanSubscription && castingStoreAvailableCustomerPlanSubscriptions({ customer, offeredService: os })?.some(_ => _.validated_at) && customerPlanSubscriptionExpirationAction?.expiration_action !== 'keep_events') {
      rows.push(i18nScoped.t('funded_by_customer_plan'))
      rows.push(i18nScoped.t('funded_by_customer_plan_reserve'))
      return rows
    }

    // Render the customerPlan price instead of the hourly price
    if (customerPlan || pendingCustomerPlanSubscription) {
      const newOrPeningCustomerPlan = pendingCustomerPlanSubscription?.customer_plan || customerPlan
      const customerPlanPriceToDisplay = newOrPeningCustomerPlan[displayPriceWithTax ? 'price_with_tax_cents' : 'price_without_tax_cents'] / 100
      const totalCustomerPlanPriceToDisplay = newOrPeningCustomerPlan.validity_duration_in_months * newOrPeningCustomerPlan[displayPriceWithTax ? 'price_with_tax_cents' : 'price_without_tax_cents'] / 100

      rows.push(<><PrimarySpan>{ currencyWrapTaxNotice(formatPriceWithDiscount(totalCustomerPlanPriceToDisplay), displayPriceWithTax) }</PrimarySpan> <Small>({ i18nScoped.t('total') })</Small></>)
      if (!newOrPeningCustomerPlan.upfront_payment) rows.push(<>{ currencyWrapTaxNotice(formatPriceWithDiscount(customerPlanPriceToDisplay), displayPriceWithTax) } <Small>({ i18nScoped.t('price_per_unit_of_work.month') })</Small></>)

      return rows
    }

    // If an available bought coupons packs is available and have enought quantity, the casting is fundes by coupons pack
    if (store.getCustomerBoughtCouponsPackInsights().remainingBoughtCouponsPacksQuantity > 0) {
      rows.push(i18nScoped.t('funded_by_coupons_pack'))
      return rows
    }

    // Render the coupon price instead of the hourly price
    if (coupon) {
      const couponPriceToDisplay = coupon[displayPriceWithTax ? 'price_with_tax_cents' : 'price_without_tax_cents'] / 100
      rows.push(<><PrimarySpan>{ currencyWrapTaxNotice(formatPriceWithDiscount(couponPriceToDisplay), displayPriceWithTax) }</PrimarySpan> <Small>({ i18nScoped.t('total') })</Small></>)
      rows.push(<>{ currencyWrapTaxNotice(formatPriceWithDiscount(couponPriceToDisplay / coupon.quantity, 'perHour'), displayPriceWithTax) } <Small>({ i18nScoped.t('price_per_unit_of_work.hour') })</Small></>)
      return rows
    }

    const sortedTotalPrices = store.getPricedEvents()

    // eslint-disable-next-line react/jsx-key
    if (!sortedTotalPrices) return [<LoadingComponent presentation="inline" align="center" loadingIconStyle={{ display: 'inline-block', height: '2em' }} />]
    if (!sortedTotalPrices.length) return []

    // It is possible for the first occurrence to last longer than the next occurrences.
    // This happen in the calculator when `calculatorResultFirstEvent` is set to an higher value than slots[].duration.
    // In that case, an additional oneshot_slot is created and the reccurring slot is delayed.
    // We render both the price of the first and the next reccurring weeks.
    //
    // If os.no_start_time_nor_duration_asked_to_customer, first_event_duration_strategy is also equal to { 'type' => 'none' } (so we do not enter this condition)
    if (sortedTotalPrices[0].first_reccurrence.displayedPriceCents !== sortedTotalPrices[0].second_reccurrence.displayedPriceCents) {
      const priceRangeFirstOccurrence = { min: sortedTotalPrices[0].first_reccurrence.displayedPriceCents, max: sortedTotalPrices[sortedTotalPrices.length - 1].first_reccurrence.displayedPriceCents }
      const priceRangeNextOccurrence = { min: sortedTotalPrices[0].second_reccurrence.displayedPriceCents, max: sortedTotalPrices[sortedTotalPrices.length - 1].second_reccurrence.displayedPriceCents }

      rows.push(<>
        <PrimarySpan>{ currencyWrapTaxNotice(renderPriceInterval(market, priceRangeFirstOccurrence, cents => formatPriceWithDiscount(cents / 100)), displayPriceWithTax) }</PrimarySpan>&nbsp;
        <Small>({ i18nScoped.t(slots.length > 1 ? ('total_first_week_' + (slots[0]?.weeks_interval ?? 1)) : 'total_first_event') })</Small>
      </>)

      rows.push(<>
        { currencyWrapTaxNotice(renderPriceInterval(market, priceRangeNextOccurrence, cents => formatPriceWithDiscount(cents / 100)), displayPriceWithTax) }&nbsp;
        <Small>({ i18nScoped.t(slots.length > 1 ? 'total_next_occurrences' : 'total_following_events') })</Small>
      </>)

      if (os.tax_credit_eligible) rows.push(<Small style={{ fontStyle: 'normal' }}>{ i18nScoped.t('tax_reduction', { rate: market.tax_credit_config.rate }) }</Small>)
      return rows
    }

    const priceRange = { min: sortedTotalPrices[0].first_reccurrence, max: sortedTotalPrices[sortedTotalPrices.length - 1].first_reccurrence }
    const invoicableQuantity = (os.rentable_unit_of_work === 'event' || os.rentable_unit_of_work === 'day')
      ? priceRange.min.priced_events.length
      : priceRange.min.priced_events
        .flatMap(ev => ev.instances)
        .map(instance => new Date(instance.end_date).getTime() - new Date(instance.start_date).getTime())
        .reduce((acc, curr) => acc + curr / 3600 / 1000, 0)
    
    const totalDisplayedPriceCentsRange = { min: priceRange.min.displayedPriceCents, max: priceRange.max.displayedPriceCents }
    const hourlyOrDailyPriceRange = { min: totalDisplayedPriceCentsRange.min / 100 / invoicableQuantity, max: totalDisplayedPriceCentsRange.max / 100 / invoicableQuantity }

    // The total amount is rendered in big font (PrimarySpan) as it is the most important price and the first visible one.
    // But when os.tax_credit_eligible is true, another price is put in first position and should retain user's focus.
    // Additionnally, the price after tax must be visible in mobile mode, where the number of rows is limitted.
    const Total = os.tax_credit_eligible ? React.Fragment : PrimarySpan

    // If os.no_start_time_nor_duration_asked_to_customer, we skip the total price row to only render the unit price.
    if (!noAskedDuration) {
      const i18nKey = slots[0]?.weeks_interval
        ? `total_recurring_per_week_interval_${slots[0]?.weeks_interval}`
        : 'total'

      rows.push(<>
        <Total>{ currencyWrapTaxNotice(renderPriceInterval(market, totalDisplayedPriceCentsRange, cents => formatPriceWithDiscount(cents / 100)), displayPriceWithTax) }</Total>&nbsp;
        <Small>({ i18nScoped.t(i18nKey) })</Small>
      </>)

      if (os.tax_credit_eligible) {
        rows.push(<>
          <PrimarySpan>{ currencyWrapTaxNotice(renderPriceInterval(market, totalDisplayedPriceCentsRange, cents => formatPriceWithDiscount(cents / 100, undefined, taxCreditFactor)), displayPriceWithTax) }</PrimarySpan>&nbsp;
          <Small>({ i18nScoped.t('after_tax_reduction', { rate: market.tax_credit_config.rate }) })</Small>
        </>)
      }
    }

    // If os.calculator_hide_duration is true, any information usable to find the duration (like unit + total price combination) is droped from UI.
    // If os.no_start_time_nor_duration_asked_to_customer, we display the hourly price instead of the total price.
    // If os.tax_credit_eligible, we skip the hourly price row to stay under the limit of 2 rows in price block (unless no other price has been given).
    if (!os.calculator_hide_duration && (noAskedDuration || !os.tax_credit_eligible)) {
      rows.push(<>
        { currencyWrapTaxNotice(renderPriceInterval(market, hourlyOrDailyPriceRange, cents => formatPriceWithDiscount(cents, 'perHour')), displayPriceWithTax) }&nbsp;
        <Small>({ i18nScoped.t(`price_per_unit_of_work.${ os.rentable_unit_of_work}`) })</Small>
      </>)
    }
  } else if (os.buyable) {
    const unitPrice = os.unit_price ? os.unit_price[displayPriceWithTax ? 'with_tax': 'without_tax'] : 0
    const totalPrice = unitPrice * (quantity || 0)
    rows.push(<><PrimarySpan>{ currencyWrapTaxNotice(formatPriceWithDiscount(totalPrice), displayPriceWithTax) }</PrimarySpan> <Small>({ i18nScoped.t('total') })</Small></>)
    rows.push(<>{ currencyWrapTaxNotice(formatPriceWithDiscount(unitPrice), displayPriceWithTax) } <Small>({ i18nScoped.t('unit_price') })</Small></>)
  }

  return rows
}

function renderPriceInterval (market: Pick<SupplierBase, 'casting_display_price_starting_at_instead_of_range?'>, range: { min: number, max: number }, formatter: (price: number) => string | JSX.Element) {
  if (range.min === range.max || market['casting_display_price_starting_at_instead_of_range?']) {
    return formatter(range.min)
  } else {
    return <>{ formatter(range.min) } - { formatter(range.max) } </>
  }
}
